import { createFeature, createReducer, on } from '@ngrx/store';
import { Asset, MultiExante, MultiExanteResponse, SavingsPlan, SavingsPlansOverview, TopInstruments } from '@zero/shared/util-models';
import { MultiSavingsPlanErrorResponse } from '../entities/multi-savings-plan-error.entity';
import { MultiSavingsPlan } from '../entities/multi-savings-plan.entity';
import { SavingsPlanSubject } from '../entities/saving-plan-search.mode';
import { savingPlansActions, savingPlansApiActions } from './saving-plans.actions';

type LoadingState = 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR';

export interface SavingPlansState {
  savingsPlansOverview: SavingsPlansOverview;
  savingsPlansOverviewErrorMessage: string | null;
  savingsPlansOverviewLoadingState: LoadingState;
  mostChosenSavingPlans: TopInstruments;
  mostChosenSavingPlansLoadingState: LoadingState;
  execDates: string[];
  execDatesLoadingState: LoadingState;
  savingsPlanSubjects: SavingsPlanSubject[];
  savingsPlanSubjectsLoadingState: LoadingState;
  multiSavingsPlans: MultiSavingsPlan[];
  execDate: string;
  lastChangedSavingsPlan: MultiSavingsPlan | undefined;
  totalAmount: number;
  isStrategy: boolean;
  confirmingSavingsPlans: boolean;
  savingsPlansCreated: boolean;
  multiSavingsPlanError: MultiSavingsPlanErrorResponse;
  intervals: string[];
  interval: string;
  loadingExante: boolean;
  exante: MultiExanteResponse;
  savingsPlanToUpdate: SavingsPlan | undefined;
  savingsPlanUpdated: boolean;
  savingsPlanUpdateError: string;
  dynamizationValues: number[];
  selectedDynamization: number;
  selectedAsset: Asset;
  existingSavingsPlanError: string;
  onboardingSavingsPlanCached: boolean;
  onboardingSavingsPlanAmount: number;
  onboardingSavingsPlanIsin: string;
  onboardingSavingsPlanAsset: Asset;
  onboardingSavingsPlanExante: MultiExante;
  submittingOnboardingSavingsPlan: boolean;
  onboardingSavingsPlanCreated: boolean;
  onboardingSavingsPlans: SavingsPlan[];
  deletingOnboardingSavingsPlan: boolean;
  onboardingSavingsPlanDeleted: boolean;
  onboardingSavingsPlanError: string;
}

export const initialState: SavingPlansState = {
  savingsPlansOverview: null,
  savingsPlansOverviewErrorMessage: null,
  savingsPlansOverviewLoadingState: 'INITIAL',
  mostChosenSavingPlans: {},
  mostChosenSavingPlansLoadingState: 'INITIAL',
  execDates: [],
  execDatesLoadingState: 'INITIAL',
  savingsPlanSubjects: [],
  savingsPlanSubjectsLoadingState: 'INITIAL',
  multiSavingsPlans: [],
  execDate: '',
  lastChangedSavingsPlan: undefined,
  totalAmount: undefined,
  isStrategy: undefined,
  confirmingSavingsPlans: false,
  savingsPlansCreated: false,
  multiSavingsPlanError: null,
  intervals: [],
  interval: '',
  loadingExante: false,
  exante: null,
  savingsPlanToUpdate: undefined,
  savingsPlanUpdated: false,
  savingsPlanUpdateError: null,
  dynamizationValues: null,
  selectedDynamization: null,
  selectedAsset: null,
  existingSavingsPlanError: null,
  onboardingSavingsPlanAmount: null,
  onboardingSavingsPlanIsin: null,
  onboardingSavingsPlanCached: false,
  onboardingSavingsPlanAsset: null,
  onboardingSavingsPlanExante: null,
  submittingOnboardingSavingsPlan: false,
  onboardingSavingsPlanCreated: false,
  onboardingSavingsPlans: null,
  onboardingSavingsPlanDeleted: false,
  deletingOnboardingSavingsPlan: false,
  onboardingSavingsPlanError: null,
};

export const savingPlansFeature = createFeature({
  name: 'savingPlans',
  reducer: createReducer(
    initialState,
    on(
      savingPlansApiActions.loadSavingsPlansOverviewFromAPI,
      (state): SavingPlansState => ({
        ...state,
        savingsPlansOverviewLoadingState: 'LOADING',
      }),
    ),
    on(
      savingPlansApiActions.loadSavingsPlansOverviewSuccess,
      (state, { savingsPlansOverview }): SavingPlansState => ({
        ...state,
        savingsPlansOverview,
        savingsPlansOverviewLoadingState: 'LOADED',
      }),
    ),
    on(
      savingPlansApiActions.loadSavingsPlansOverviewFailure,
      (state, { error }): SavingPlansState => ({
        ...state,
        savingsPlansOverviewLoadingState: 'ERROR',
        savingsPlansOverviewErrorMessage: error.message,
      }),
    ),
    on(
      savingPlansApiActions.loadMostChosenSavingPlansFromAPI,
      (state): SavingPlansState => ({
        ...state,
        mostChosenSavingPlansLoadingState: 'LOADING',
      }),
    ),
    on(
      savingPlansApiActions.loadMostChosenSavingPlansSuccess,
      (state, { mostChosenSavingPlans }): SavingPlansState => ({
        ...state,
        mostChosenSavingPlans,
        mostChosenSavingPlansLoadingState: 'LOADED',
      }),
    ),
    on(
      savingPlansApiActions.loadMostChosenSavingPlansFailure,
      (state): SavingPlansState => ({
        ...state,
        mostChosenSavingPlansLoadingState: 'ERROR',
      }),
    ),
    on(
      savingPlansApiActions.loadExecDatesFromAPI,
      (state): SavingPlansState => ({
        ...state,
        execDatesLoadingState: 'LOADING',
      }),
    ),
    on(
      savingPlansApiActions.loadExecDatesSuccess,
      (state, { execDates }): SavingPlansState => ({
        ...state,
        execDates,
        execDatesLoadingState: 'LOADED',
      }),
    ),
    on(
      savingPlansApiActions.loadExecDatesFailure,
      (state): SavingPlansState => ({
        ...state,
        execDatesLoadingState: 'ERROR',
      }),
    ),
    on(
      savingPlansApiActions.loadSavingsPlanSubjectsFromAPI,
      (state): SavingPlansState => ({
        ...state,
        savingsPlanSubjectsLoadingState: 'LOADING',
      }),
    ),
    on(
      savingPlansApiActions.loadSavingsPlanSubjectsSuccess,
      (state, { savingsPlanSubjects }): SavingPlansState => ({
        ...state,
        savingsPlanSubjects,
        savingsPlanSubjectsLoadingState: 'LOADED',
      }),
    ),
    on(
      savingPlansApiActions.loadSavingsPlanSubjectsFailure,
      (state): SavingPlansState => ({
        ...state,
        savingsPlanSubjectsLoadingState: 'ERROR',
      }),
    ),
    on(
      savingPlansActions.addAssetToForm,
      (state, { asset }): SavingPlansState => ({
        ...state,
        isStrategy: false,
        multiSavingsPlans: [...state.multiSavingsPlans, { isin: asset.isin, asset }],
      }),
    ),
    on(
      savingPlansActions.removeSavingsPlansFromForm,
      (state, { savingsPlansToRemove }): SavingPlansState => ({
        ...state,
        isStrategy: false,
        multiSavingsPlans: state.multiSavingsPlans.filter((savingsPlanToKeep) => !savingsPlansToRemove.includes(savingsPlanToKeep)),
      }),
    ),
    on(
      savingPlansActions.clearForm,
      (state): SavingPlansState => ({
        ...state,
        multiSavingsPlans: [],
        execDate: '',
        lastChangedSavingsPlan: undefined,
        totalAmount: undefined,
        isStrategy: undefined,
        confirmingSavingsPlans: false,
        savingsPlansCreated: false,
        savingsPlansOverviewErrorMessage: null,
        multiSavingsPlanError: null,
        intervals: [],
        interval: '',
        loadingExante: false,
        exante: null,
        savingsPlanToUpdate: undefined,
        savingsPlanUpdated: false,
        savingsPlanUpdateError: null,
        selectedDynamization: null,
        selectedAsset: null,
        existingSavingsPlanError: null,
      }),
    ),
    on(savingPlansActions.changeExecDate, (state, { execDate }): SavingPlansState => ({ ...state, execDate })),
    on(savingPlansActions.updateAmount, (state, { savingsPlan, amount }): SavingPlansState => {
      const multiSavingsPlans = state.multiSavingsPlans.map((s) => (s.isin !== savingsPlan.isin ? s : { ...s, amount }));
      return {
        ...state,
        multiSavingsPlans,
        isStrategy: false,
        lastChangedSavingsPlan: multiSavingsPlans.find((s) => s.isin === savingsPlan.isin),
      };
    }),
    on(savingPlansActions.updateAmountSilently, (state, { savingsPlan }): SavingPlansState => {
      const multiSavingsPlans = state.multiSavingsPlans.map((s) => {
        const amount = calcAmount(s.share, state.totalAmount);
        return s.isin !== savingsPlan.isin ? s : { ...s, amount, amountAsText: amountAsText(amount) };
      });
      return {
        ...state,
        multiSavingsPlans,
        lastChangedSavingsPlan: multiSavingsPlans.find((s) => s.isin === savingsPlan.isin),
      };
    }),
    on(
      savingPlansActions.setError,
      (state, { savingsPlan, error }): SavingPlansState => ({
        ...state,
        multiSavingsPlans: state.multiSavingsPlans.map((s) => (s.isin !== savingsPlan.isin ? s : { ...s, error })),
      }),
    ),
    on(
      savingPlansActions.addAssetsToForm,
      (state, { savingsPlans, totalAmount }): SavingPlansState => ({
        ...state,
        totalAmount,
        isStrategy: savingsPlans.length > 1 ? true : null,
        multiSavingsPlans: savingsPlans.map((s) => {
          const amount = calcAmount(s.share, totalAmount);
          return { ...s, amount, amountAsText: amountAsText(amount) };
        }),
      }),
    ),
    on(savingPlansApiActions.addAssetsToFormSuccess, (state, { savingsPlans }): SavingPlansState => {
      const multiSavingsPlans = state.multiSavingsPlans.map((msp) => {
        const foundMsp = savingsPlans.find((sp) => sp.isin === msp.isin);
        return foundMsp ? { ...msp, asset: foundMsp.asset } : msp;
      });
      return { ...state, multiSavingsPlans };
    }),
    on(
      savingPlansActions.changeTotalAmount,
      (state, { totalAmount }): SavingPlansState => ({
        ...state,
        totalAmount,
      }),
    ),
    on(savingPlansActions.createSavingsPlans, (state): SavingPlansState => ({ ...state, confirmingSavingsPlans: true })),
    on(
      savingPlansApiActions.createSavingsPlansSuccess,
      (state): SavingPlansState => ({ ...state, confirmingSavingsPlans: false, savingsPlansCreated: true, multiSavingsPlanError: null }),
    ),
    on(
      savingPlansApiActions.createSavingsPlansFailure,
      (state, { error }): SavingPlansState => ({
        ...state,
        confirmingSavingsPlans: false,
        savingsPlansCreated: false,
        multiSavingsPlanError: error,
      }),
    ),
    on(
      savingPlansActions.setSavingsPlanToUpdate,
      (state, { savingsPlan }): SavingPlansState => ({ ...state, savingsPlanToUpdate: savingsPlan }),
    ),
    on(savingPlansActions.updateSavingsPlan, (state): SavingPlansState => ({ ...state, savingsPlanUpdated: false })),
    on(
      savingPlansApiActions.updateSavingsPlansSuccess,
      (state): SavingPlansState => ({ ...state, savingsPlanToUpdate: undefined, savingsPlanUpdated: true, savingsPlanUpdateError: null }),
    ),
    on(
      savingPlansApiActions.updateSavingsPlansFailure,
      (state, { error }): SavingPlansState => ({
        ...state,
        savingsPlanUpdateError: error.message,
      }),
    ),
    on(savingPlansActions.clearSavingsPlanUpdateError, (state): SavingPlansState => ({ ...state, savingsPlanUpdateError: null })),
    on(
      savingPlansApiActions.loadOptionsSuccess,
      (state, { options }): SavingPlansState => ({
        ...state,
        execDates: options.execDates,
        intervals: options.intervals,
        dynamizationValues: options.dynamizationValues?.filter((value) => value !== 0),
      }),
    ),
    on(savingPlansActions.changeInterval, (state, { interval }): SavingPlansState => ({ ...state, interval })),
    on(savingPlansActions.loadExante, (state): SavingPlansState => ({ ...state, loadingExante: true, exante: null })),
    on(
      savingPlansApiActions.loadExanteSuccess,
      (state, { multiExante }): SavingPlansState => ({ ...state, loadingExante: false, exante: multiExante }),
    ),
    on(savingPlansApiActions.loadExanteFailure, (state): SavingPlansState => ({ ...state, loadingExante: false })),
    on(savingPlansActions.revokeStrategy, (state): SavingPlansState => ({ ...state, isStrategy: false })),
    on(savingPlansActions.clearMultiSavingsPlanError, (state): SavingPlansState => ({ ...state, multiSavingsPlanError: null })),
    on(
      savingPlansActions.removeLatestSavingsPlan,
      (state): SavingPlansState => ({ ...state, multiSavingsPlans: state.multiSavingsPlans.slice(0, -1) }),
    ),
    on(savingPlansActions.setDynamization, (state, { value }): SavingPlansState => ({ ...state, selectedDynamization: value })),
    on(
      savingPlansApiActions.loadExistingSavingsPlanSuccess,
      (state, { savingsPlan }): SavingPlansState => ({
        ...state,
        savingsPlanToUpdate: savingsPlan.plan,
        selectedAsset: savingsPlan.asset,
        selectedDynamization: savingsPlan.plan?.dynamization,
      }),
    ),
    on(
      savingPlansActions.setExistingSavingsPlanError,
      (state, { error, amount }): SavingPlansState => ({
        ...state,
        existingSavingsPlanError: error,
        savingsPlanToUpdate: {
          ...state.savingsPlanToUpdate,
          amount,
        },
      }),
    ),
    on(
      savingPlansActions.cacheOnboardingSavingsPlan,
      (state, { amount, isin }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanAmount: amount,
        onboardingSavingsPlanIsin: isin,
        onboardingSavingsPlanCached: true,
      }),
    ),
    on(
      savingPlansActions.clearOnboardingSavingsPlanCache,
      (state): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanCached: false,
        onboardingSavingsPlanAsset: null,
      }),
    ),
    on(
      savingPlansActions.clearOnboardingSavingsPlanForm,
      (state): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanCached: false,
        onboardingSavingsPlanAsset: null,
        onboardingSavingsPlanAmount: null,
        onboardingSavingsPlanExante: null,
        submittingOnboardingSavingsPlan: false,
        onboardingSavingsPlanCreated: false,
      }),
    ),
    on(
      savingPlansActions.loadOnboardingSavingsPlanInfos,
      (state): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanAsset: null,
      }),
    ),
    on(
      savingPlansApiActions.loadOnboardingSavingsPlanInfosSuccess,
      (state, { asset }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanAsset: asset,
      }),
    ),
    on(
      savingPlansApiActions.loadOnboardingSavingsPlanExanteSuccess,
      (state, { exante }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanExante: exante?.responses[0],
      }),
    ),
    on(
      savingPlansApiActions.loadOnboardingSavingsPlanExanteSuccess,
      (state, { exante }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanExante: exante?.responses[0],
      }),
    ),
    on(
      savingPlansActions.submitOnboardingSavingsPlan,
      (state): SavingPlansState => ({
        ...state,
        submittingOnboardingSavingsPlan: true,
      }),
    ),
    on(
      savingPlansApiActions.submitOnboardingSavingsPlanSuccess,
      (state): SavingPlansState => ({
        ...state,
        submittingOnboardingSavingsPlan: false,
        onboardingSavingsPlanCreated: true,
      }),
    ),
    on(
      savingPlansApiActions.submitOnboardingSavingsPlanFailure,
      (state, { error }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlanError: error?.message,
        submittingOnboardingSavingsPlan: false,
        onboardingSavingsPlanCreated: false,
      }),
    ),
    on(
      savingPlansApiActions.loadOnboardingSavingsPlansSuccess,
      (state, { savingsPlans }): SavingPlansState => ({
        ...state,
        onboardingSavingsPlans: savingsPlans.filter((plan) => plan.status === 'created'),
      }),
    ),
    on(
      savingPlansActions.deleteOnboardingSavingsPlan,
      (state): SavingPlansState => ({
        ...state,
        deletingOnboardingSavingsPlan: true,
      }),
    ),
    on(
      savingPlansApiActions.deleteOnboardingSavingsPlanSuccess,
      (state): SavingPlansState => ({
        ...state,
        deletingOnboardingSavingsPlan: false,
        onboardingSavingsPlanDeleted: true,
      }),
    ),
    on(
      savingPlansApiActions.deleteOnboardingSavingsPlanFailure,
      (state): SavingPlansState => ({
        ...state,
        deletingOnboardingSavingsPlan: false,
        onboardingSavingsPlanDeleted: false,
      }),
    ),
    on(
      savingPlansActions.clearOnboardingSavingsPlanDeletionState,
      (state): SavingPlansState => ({
        ...state,
        deletingOnboardingSavingsPlan: false,
        onboardingSavingsPlanDeleted: false,
      }),
    ),
  ),
});

const calcAmount = (share: number, totalAmount: number) => (totalAmount ? parseFloat(((share / 100) * totalAmount).toFixed(3)) : null);
const amountAsText = (amount: number) => (amount ? amount.toFixed(2).replace('.', ',') : null);
